import { BUTTON_SIZE, COLORS, DSButton, DSModal } from '@repo/design-system/index'
import { QIcon_check_default, QIcon_check_mark, QIcon_close_default } from '@repo/icons/index'
import { useEffect } from 'react'
import { GTM_CLICK_EVENT, useEventTracking } from '../../../../helpers/gtmHelper'
import { getJwtToken } from '../../../../modules/auth'
import { APP_SCREEN_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsInApp from '../../../../modules/hooks/common/useIsInApp'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import useReturnFullUrl from '../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_pricing, s_schedule } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import BottomSheet from './BottomSheet'
import { BenefitBadge } from './PackageOptionCard'
import { PromotionCode } from './PricingHero'
import { productTypeEnum, useProductContext } from './ProductContext'

export const PackageItemModal = ({ product, isOpen, setIsOpen }) => {
  return (
    <DSModal
      id={'PackageItemModal'}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      closeOnDim
      size={'custom'}
      className="p-32 bg-white rounded-[12px]"
    >
      <PackageItemBody product={product} setIsOpen={setIsOpen} />
    </DSModal>
  )
}

export const PackageItemBottomSheet = ({ product, isOpen, setIsOpen }) => {
  return (
    <BottomSheet isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <div className="px-20">
        <PackageItemBody product={product} />
      </div>
    </BottomSheet>
  )
}

const PackageItemBody = ({ product, isNewVersion = false, setIsOpen = null }) => {
  const isMobile = useIsMobile()
  const { t } = useTranslation()
  const isWebview = useIsWebview()
  const isInApp = useIsInApp()
  const router = useAppRouter()
  const getFullUrl = useReturnFullUrl()
  const { sendClickEvent } = useEventTracking()
  const isApp = router.query.app ? true : false

  const { productType, purchaseInfo, setPurchaseInfo, convertedCountryCode, applicablePromotionId } =
    useProductContext()
  const { id: purchaseId } = purchaseInfo || {}

  const hasPromotionEligibility = applicablePromotionId == PromotionCode.SecretEncore
  const newVersion = true

  const handlePurchaseClick = () => {
    const eventName = isMobile
      ? GTM_CLICK_EVENT.PURCHASE_LESSON_OPTION_SELECT_BTN_MOBILE
      : GTM_CLICK_EVENT.PURCHASE_LESSON_OPTION_SELECT_BTN
    sendClickEvent({ button_name: eventName })

    let purchaseUrl = ''
    if (newVersion) {
      purchaseUrl = `${urls.student.newPortal.pricing.detail(
        purchaseId
      )}?country_code=${convertedCountryCode}&app=${isApp}`
    } else {
      const isCombined = productType.type === productTypeEnum.RINGLE_PACKAGE
      purchaseUrl = isCombined
        ? `${urls.student.newPortal.pricing.combined.detail(
            purchaseId
          )}?country_code=${convertedCountryCode}&app=${isApp}`
        : `${urls.student.newPortal.pricing.detail(purchaseId)}?country_code=${convertedCountryCode}&app=${isApp}`
    }

    if (!getJwtToken()) {
      const url = `${urls.student.auth.new}?redirect_url=${purchaseUrl}`

      if (isWebview && !isInApp) {
        router.push(url, {
          appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
          appSendData: { url: getFullUrl(url) },
        })
      } else {
        router.push(url)
      }
    } else {
      if (isWebview && !isInApp) {
        router.push(purchaseUrl, {
          appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
          appSendData: { url: getFullUrl(purchaseUrl) },
        })
      } else {
        router.push(purchaseUrl)
      }
    }
  }

  useEffect(() => {
    setPurchaseInfo((prev) => ({ ...prev, id: product.weeklyLesson.id }))
  }, [])

  if (!product) return null

  const { flexibleLesson, weeklyLesson } = product
  const packageItems = [
    {
      title: n_pricing.every_week_n.title,
      ...weeklyLesson,
      discountValue: false,
      eventName: isMobile
        ? GTM_CLICK_EVENT.PURCHASE_LESSON_OPTION_EVERY_WEEK_MOBILE
        : GTM_CLICK_EVENT.PURCHASE_LESSON_OPTION_EVERY_WEEK,
    },
    {
      title: n_pricing.none_n.title,
      ...flexibleLesson,
      discountValue: weeklyLesson.weekly_lesson_discount.amount.formatted,
      eventName: isMobile
        ? GTM_CLICK_EVENT.PURCHASE_LESSON_OPTION_FLEXIBLE_MOBILE
        : GTM_CLICK_EVENT.PURCHASE_LESSON_OPTION_FLEXIBLE,
    },
  ]

  return (
    <>
      <section className="pb-16 flex flex-col gap-y-16">
        <div className="flex justify-between">
          <h1 className="Title2-18Bd sm:Headline3-22Bd">{t(n_pricing.lesson_option_modal.title)}</h1>
          {isMobile || (
            <button>
              <QIcon_close_default width={24} height={24} color={COLORS.BLACK} onClick={() => setIsOpen(false)} />
            </button>
          )}
        </div>
        <section className="flex sm:flex-row flex-col gap-16">
          {packageItems.map((packageItem) => (
            <button
              key={packageItem.id}
              className={`p-20 w-full sm:flex sm:flex-col sm:w-[280px] sm:min-h-[281px] border rounded-lg ${
                purchaseId === packageItem.id ? 'border-purple-500 bg-gray-50' : ''
              }`}
              onClick={() => {
                setPurchaseInfo((prev) => ({ ...prev, id: packageItem.id }))
                sendClickEvent({ button_name: packageItem.eventName })
              }}
            >
              {isMobile || (
                <>
                  <div className="w-full flex justify-end">
                    <div className={`h-24 ${packageItem.discountValue ? 'w-full flex justify-between' : ''}`}>
                      {!!packageItem.discountValue && (
                        <BenefitBadge
                          title={t(n_pricing.label.plus_amount(packageItem.discountValue))}
                          className="bg-blue-50 text-blue-500 border-blue-200"
                        />
                      )}
                      {purchaseId === packageItem.id && (
                        <QIcon_check_default width={24} height={24} color={COLORS.PURPLE600} />
                      )}
                    </div>
                  </div>
                  <div className="py-16 w-full flex flex-col border-b text-left">
                    <span className="Title1-20Bd">{t(packageItem.title)}</span>
                    <span className="Body1-16Md">{packageItem.prices.sale_price.formatted}</span>
                  </div>
                </>
              )}
              {isMobile && (
                <>
                  <div className="flex justify-between w-full border-b">
                    <div className="pb-16 flex flex-col text-left gap-y-8">
                      {!!packageItem.discountValue && (
                        <BenefitBadge
                          title={t(n_pricing.label.plus_amount(packageItem.discountValue))}
                          className="bg-blue-50 text-blue-500 border-blue-200"
                        />
                      )}
                      <div className="flex flex-col">
                        <span className="Title1-20Bd">{t(packageItem.title)}</span>
                        <span className="Body1-16Md">{t(packageItem.prices.sale_price.formatted)}</span>
                      </div>
                    </div>
                    <div className="">
                      {purchaseId === packageItem.id && (
                        <QIcon_check_default width={24} height={24} color={COLORS.PURPLE600} />
                      )}
                    </div>
                  </div>
                </>
              )}

              <ul className="py-20 flex flex-col gap-y-8">
                {packageItem.is_weekly_lesson_package || (
                  <li className="flex gap-x-8 items-center text-left">
                    <div>
                      <QIcon_check_mark width={16} height={16} color={COLORS.GRAY400} />
                    </div>
                    <span className="Body2-14Md text-gray-600">{t(n_pricing.lesson_option_modal.none_1)}</span>
                  </li>
                )}
                {packageItem.is_weekly_lesson_package || (
                  <li className="flex gap-x-8 items-center text-left">
                    <div>
                      <QIcon_check_mark width={16} height={16} color={COLORS.GRAY400} />
                    </div>
                    <span className="Body2-14Md text-gray-600">{t(n_pricing.lesson_option_modal.none_2)}</span>
                  </li>
                )}
                {packageItem.is_weekly_lesson_package && (
                  <li className="flex gap-x-8 items-center text-left">
                    <div>
                      <QIcon_check_mark width={16} height={16} color={COLORS.GRAY400} />
                    </div>
                    <span className="Body2-14Md text-gray-600">{t(n_pricing.lesson_option_modal.week_1)}</span>
                  </li>
                )}
                {packageItem.is_weekly_lesson_package && !hasPromotionEligibility && (
                  <li className="flex gap-x-8 items-center text-left">
                    <div>
                      <QIcon_check_mark width={16} height={16} color={COLORS.GRAY400} />
                    </div>
                    <span className="Body2-14Md text-gray-600">{t(n_pricing.lesson_option_modal.week_2)}</span>
                  </li>
                )}
                {packageItem.is_weekly_lesson_package && hasPromotionEligibility && (
                  <li className="flex gap-x-8 items-center text-left">
                    <div>
                      <QIcon_check_mark width={16} height={16} color={COLORS.GRAY400} />
                    </div>
                    <div>
                      <span className="Body2-14Md text-gray-400 line-through">
                        {t(n_pricing.lesson_option_modal.week_2)}
                      </span>
                      <br />
                      <span className="Body2-14Md text-gray-600">{t(n_pricing.lesson_option_modal.week_2_secret)}</span>
                    </div>
                  </li>
                )}
                {packageItem.bonuses.deduction_grace_count > 0 && (
                  <li className="flex gap-x-8 items-center text-left">
                    <div>
                      <QIcon_check_mark width={16} height={16} color={COLORS.GRAY400} />
                    </div>
                    <div>
                      <span className="Body2-14Md text-gray-600">
                        {t(n_pricing.lesson_option_modal.postphone(packageItem.bonuses.deduction_grace_count))}
                      </span>
                    </div>
                  </li>
                )}
              </ul>
            </button>
          ))}
        </section>
        <ul>
          <li className="Caption-12Md sm:Body2-14Md text-gray-500">{t(n_pricing.lesson_option_modal.policy_1)}</li>
          <li className="Caption-12Md sm:Body2-14Md text-gray-500">{t(n_pricing.lesson_option_modal.policy_2)}</li>
        </ul>
      </section>
      <div className="sm:static sticky bottom-0 left-0 pb-36 sm:pb-0 pt-12 w-full flex justify-end bg-white">
        <DSButton
          className="sm:w-1/3 w-full"
          size={BUTTON_SIZE.LG}
          customBgColor={COLORS.PURPLE600}
          onClick={() => handlePurchaseClick()}
        >
          <DSButton.Label>{t(s_schedule.selected)}</DSButton.Label>
        </DSButton>
      </div>
    </>
  )
}

export default PackageItemBody
